import React from 'react';
import { Moment } from 'moment';
import { faBoxes } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Alert,
	Flex,
	Space,
} from 'antd';
import { Link } from 'react-router-dom';
import {
	CargoContractSides,
	DATE_AND_TIME,
	LaytimeCalculationMethods,
	LaytimeIntervals,
	LaytimeSettlementTypes,
	LaytimeTerms,
	PortActionTypes,
} from '@shared/utils/constants';
import { isLoadPort } from '@shared/utils/cargoPortHelpers';
import {
	formatDate,
	getUtcOffset,
} from '@shared/utils/date';
import normalize from '@shared/utils/normalizeNumber';
import { round } from '@shared/utils/math';
import { formatCurrency } from '@shared/utils/currency';
import { getDemurrageTermLabel } from '@shared/utils/demurrageTerm';
import { splitActionKey } from '@shared/utils/splitActionKey';
import type { CargoProps } from '@api/models/cargo';
import type {
	CargoPortWithTurnDays,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import type { Port } from '@api/utils/ports';
import { UpdateCargoPortRequest } from '@api/features/cargos/updateCargoPort';
import { EditableDetailsItem } from '@client/components/EditableField';
import TooltipIcon from '@client/components/TooltipIcon';
import { Links } from '@client/utils/links';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import { SPLIT_BL_TOOLTIP } from './cargoUtils';
import styles from './styles.module.css';

type Items = {
	keyItems: EditableDetailsItem<Partial<any>>[];
	cargoPortItems: {
		id: number;
		port: Port;
		portAndActionKey: string;
		items: EditableDetailsItem<Partial<any>>[];
	};
};

const getCargoSideLabel = (cargo: CargoProps) => (
	cargo.contractSide === CargoContractSides.VC_IN ? 'IN' : 'OUT'
);

const createCargoLinkField = (
	cargo: CargoProps,
): EditableDetailsItem<Partial<CargoProps>> => {
	const label = getCargoSideLabel(cargo);

	return {
		key: 'contractSide',
		label: 'Cargo',
		type: 'text',
		value: label,
		render: () => (
			<Link to={Links.Cargo.get(cargo.id)}>
				<Space>
					<FontAwesomeIcon icon={faBoxes} />
					{label}
				</Space>
			</Link>
		),
	};
};

const createLaytimeTermsField = (cargo: CargoProps): EditableDetailsItem<Partial<CargoProps>> => ({
	key: 'laytimeTerms',
	label: 'Laytime terms',
	editable: true,
	type: 'select',
	options: Object.values(LaytimeTerms).map((terms) => ({
		value: terms,
		label: terms,
	})),
	value: cargo.laytimeTerms,
	inputProps: {
		'data-testid': 'laytimeTerms'
	}
});

const createLaytimeAllowedField = (
	key: string,
	value: number,
	label = 'Calculated laytime allowed',
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort?: CargoPortWithTurnDays,
): EditableDetailsItem<Partial<CargoProps>> => {
	let intervalLabel = `${cargo?.timeAllowedInterval}${value === 1 ? '' : 's'}`;

	if (cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
		intervalLabel = `${cargoPort?.laytimeInterval}${value === 1 ? '' : 's'}`;
	}

	return ({
		key,
		type: 'number',
		label,
		value: round(value, 6),
		editable: true,
		inputProps: {
			suffix: intervalLabel,
			disabled: true
		},
		render: (v) => `${round(v.value, 6)} ${intervalLabel}`,
	});
};

const createLaytimeAllowedOverrideField = (
	key: string,
	value: number | null,
	label = 'Laytime allowed',
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort?: CargoPortWithTurnDays,
): EditableDetailsItem<Partial<CargoProps>> => {
	let intervalLabel = `${cargo?.timeAllowedInterval}${value === 1 ? '' : 's'}`;

	if (cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
		intervalLabel = `${cargoPort?.laytimeInterval}${value === 1 ? '' : 's'}`;
	}

	return ({
		key,
		type: 'number',
		label,
		value,
		editable: true,
		inputProps: {
			suffix: intervalLabel,
			'data-testid': 'laytimeAllowed'
		},
		render: (v) => `${round(v.value, 6)} ${intervalLabel}`,
	});
};

const createLaytimeUsedField = (
	value: number | undefined,
	cargo: CargoProps,
	cargoPort: CargoPortWithTurnDays,
	label = 'Laytime used',
): EditableDetailsItem<Partial<CargoProps>> => {
	let interval = cargo.timeAllowedInterval;

	if (cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
		interval = cargoPort.laytimeInterval;
	}

	return ({
		key: 'laytimeUsed',
		label,
		value,
		render: (v) => {
			let val = v.value / 60 / 24;

			if (interval === LaytimeIntervals.HOUR) {
				val = v.value / 60;
			}

			return `${round(val, 6)} ${interval}${val === 1 ? '' : 's'}`;
		},
	});
};

const createTimeDifferenceField = (
	timeAllowed: number,
	timeUsed: number | undefined,
	cargo: CargoProps,
	cargoPort?: CargoPortWithTurnDays,
): EditableDetailsItem<Partial<CargoProps>> => {
	let interval = cargo.timeAllowedInterval;

	if (cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE && cargoPort) {
		interval = cargoPort.laytimeInterval;
	}

	const usedValue = timeUsed ?? 0;
	const allowedVal = timeAllowed;
	let usedVal = usedValue / 60 / 24;

	if (interval === LaytimeIntervals.HOUR) {
		usedVal = usedValue / 60;
	}

	const difference = allowedVal - usedVal;
	const isTimeLost = difference < 0;
	const absValue = Math.abs(difference);
	const label = isTimeLost ? 'Time lost' : 'Time gained';

	return ({
		key: 'timeDifference',
		label,
		value: absValue,
		render: () => (<b>{`${round(absValue, 6)} ${interval}${absValue === 1 ? '' : 's'}`}</b>),
	});
};

const createSettlementField = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort: CargoPortWithTurnDays,
	handleCargoChange: (values: Partial<CargoWithPortsAndCalculationsJSON>, shouldSave?: boolean) => void,
	handleCargoPortChange: (values: Partial<UpdateCargoPortRequest['attributes']>, shouldSave?: boolean) => void,
	isSmall: boolean,
	settlementType: LaytimeSettlementTypes,
): EditableDetailsItem<Partial<CargoProps>> => {
	const { action } = splitActionKey(cargoPort.portAndActionKey);
	const demurrageLabel = getDemurrageTermLabel(cargo.demurrageTerm);

	let settlementAmount: number | null;
	let updateSettlementTypeKey = 'settlementType';
	let updateSettlementAmountKey = 'settlementAmount';
	let handleUpdate: typeof handleCargoPortChange | typeof handleCargoChange;

	switch (cargo.laytimeTerms) {
		default:
		case LaytimeTerms.NON_REVERSIBLE: {
			settlementType = cargoPort.settlementType;
			settlementAmount = cargoPort.settlementAmount;
			handleUpdate = handleCargoPortChange;
			break;
		}

		case LaytimeTerms.REVERSIBLE: {
			settlementType = cargo.settlementType;
			settlementAmount = cargo.settlementAmount;
			handleUpdate = handleCargoChange;
			break;
		}

		case LaytimeTerms.AVERAGED: {
			if (action === PortActionTypes.LOADING) {
				settlementType = cargo.settlementTypeLoading;
				settlementAmount = cargo.settlementAmountLoading;
				updateSettlementTypeKey = 'settlementTypeLoading';
				updateSettlementAmountKey = 'settlementAmountLoading';
			} else {
				settlementType = cargo.settlementTypeDischarging;
				settlementAmount = cargo.settlementAmountDischarging;
				updateSettlementTypeKey = 'settlementTypeDischarging';
				updateSettlementAmountKey = 'settlementAmountDischarging';
			}

			handleUpdate = handleCargoChange;
		}
	}

	return {
		key: 'settlement',
		label: settlementAmount != null ?
			(<b>Settlement</b>) :
			'Settlement',
		render: () => (
			<Flex gap={10} vertical={isSmall}>
				<Select
					data-testid="settlementType"
					key={settlementType}
					options={[{
						label: demurrageLabel,
						value: LaytimeSettlementTypes.DEMURRAGE,
					}, {
						label: LaytimeSettlementTypes.DESPATCH,
						value: LaytimeSettlementTypes.DESPATCH,
					}]}
					className={styles.settlementSelect}
					allowClear={false}
					value={settlementType}
					onSelect={(v) => handleUpdate({ [updateSettlementTypeKey]: v! })}
				/>
				<NumericInput
					data-testid="settlementAmount"
					allowNegative={false}
					value={settlementAmount}
					fullWidth={false}
					width={50}
					onChange={(v) => handleUpdate({ [updateSettlementAmountKey]: v })}
					suffix={cargo.currency}
					allowClear
					onClear={() => handleUpdate({ [updateSettlementAmountKey]: null }, true)}
					addonAfter={null}
				/>
			</Flex>
		)
	};
};

const createDateField = ({
	key,
	label,
	value,
	defaultPortTzOffset,
	minDate,
	maxDate,
	defaultPickerValue,
}: {
	key: 'laytimeFrom' | 'laytimeTo';
	label: string;
	value: Moment | null;
	defaultPortTzOffset: number;
	minDate?: Moment | undefined;
	maxDate?: Moment | undefined;
	defaultPickerValue?: Moment | null;
}): EditableDetailsItem<Partial<CargoPortWithTurnDays>> => ({
	key,
	label,
	type: 'date',
	value,
	editable: true,
	inputProps: {
		showTimezone: true,
		time: true,
		defaultUtcOffset: defaultPortTzOffset,
		forceUtcUpdate: true,
		minDate,
		maxDate,
		defaultPickerValue: defaultPickerValue ?? undefined,
		allowClear: true,
		'data-testid': key,
	},
	render: () => (value ? formatDate(value, DATE_AND_TIME, true) : (<em>-</em>)),
});

const createCargoPortItems = (
	cargoPort: CargoPortWithTurnDays,
	cargo: CargoWithPortsAndCalculationsJSON,
	laytimeFrom?: Moment | null,
	laytimeTo?: Moment | null,
): EditableDetailsItem<Partial<CargoPortWithTurnDays>>[] => {
	const isLoading = isLoadPort(cargoPort);
	const defaultPortTzOffset = getUtcOffset(cargoPort.port);

	const getDeadfreightSuffix = () => {
		if (!cargoPort.includeDeadfreight || cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
			return undefined;
		}

		return (
			<Alert
				type="warning"
				message="You may need to recalculate time allowed if deadfreight is invoiced"
				style={{ padding: 4 }}
			/>
		);
	};

	return [
		{
			key: 'blQuantity',
			label: cargoPort.hasSplitBl ? (
				<Space size={0}>
					BL Quantity
					<TooltipIcon>
						{SPLIT_BL_TOOLTIP}
					</TooltipIcon>
				</Space>
			) : 'BL Quantity',
			type: 'number',
			value: cargoPort.blQuantity,
			editable: true,
			inputProps: {
				disabled: cargoPort.timeAllowedOverride != null || cargoPort.hasSplitBl,
				suffix: `${cargo.unit}`,
			},
		},
		{
			key: 'loadingRate',
			label: `${isLoading ? 'Loading' : 'Discharging'} rate`,
			type: 'number',
			value: cargoPort.loadingRate,
			editable: true,
			inputProps: {
				disabled: cargoPort.timeAllowedOverride != null,
				suffix: `${cargo.unit} / ${cargoPort.laytimeInterval}`,
			},
		},
		...(cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE ? [
			createLaytimeAllowedOverrideField(
				'timeAllowedOverride',
				cargoPort.timeAllowedOverride,
				'Input laytime allowed',
				cargo,
				cargoPort,
			),
		] : []),
		{
			key: 'includeDeadfreight',
			label: 'Include deadfreight',
			type: 'checkbox',
			value: cargoPort.includeDeadfreight,
			editable: true,
			inputProps: {
				suffix: getDeadfreightSuffix(),
				suffixStyleProps: {
					color: 'gray',
				},
			},
		},
		...(cargoPort.includeDeadfreight ? [
			{
				key: 'laytimeQuantityOverride',
				label: 'Laytime quantity',
				type: 'number' as 'number',
				value: cargoPort.laytimeQuantityOverride,
				editable: true,
				inputProps: {
					suffix: `${cargo.unit} / ${cargoPort.laytimeInterval}`,
				},
			},
		] : []),
		{
			key: 'calculationMethod',
			label: 'Laytime calculation method',
			value: cargoPort.calculationMethod,
			type: 'select',
			options: Object.values(LaytimeCalculationMethods).map((method) => ({
				value: method,
				label: method,
			})),
			editable: true,
		},
		{
			key: 'turnTime',
			label: 'Turn days',
			value: cargoPort.turnDays,
			render: (v: { value: number }) => v.value ? `${v.value} day${v.value === 1 ? '' : 's'}` : 0,
		},
		...(cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE ? [
			{
				key: 'demurrage',
				label: `${getDemurrageTermLabel(cargo.demurrageTerm)} rate`,
				type: 'number',
				value: cargoPort.demurrage,
				editable: true,
				inputProps: {
					'data-testid': 'demurrageRate',
					suffix: `${cargo.currency} / ${cargo.demurrageDespatchInterval}`,
				},
			} as EditableDetailsItem<Partial<CargoPortWithTurnDays>>,
			{
				key: 'despatch',
				label: 'Despatch rate',
				type: 'number',
				value: cargoPort.despatch,
				editable: true,
				inputProps: {
					'data-testid': 'despatchRate',
					suffix: `${cargo.currency} / ${cargo.demurrageDespatchInterval}`,
				},
			} as EditableDetailsItem<Partial<CargoPortWithTurnDays>>,
		] : []),
		createDateField({
			key: 'laytimeFrom',
			label: 'Laytime to count from',
			value: cargoPort.laytimeFrom,
			defaultPortTzOffset,
			maxDate: laytimeTo ?? undefined,
			defaultPickerValue: cargoPort.defaultDate,
		}),
		createDateField({
			key: 'laytimeTo',
			label: 'Laytime to count to',
			value: cargoPort.laytimeTo,
			defaultPortTzOffset,
			minDate: laytimeFrom ?? undefined,
			defaultPickerValue: cargoPort.defaultDate,
		}),
	];
};

export const getNonReversibleFields = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort: CargoPortWithTurnDays,
	handleCargoChange: (values: Partial<CargoWithPortsAndCalculationsJSON>, shouldSave?: boolean) => void,
	handleCargoPortChange: (values: Partial<UpdateCargoPortRequest['attributes']>, shouldSave?: boolean) => void,
	isSmall: boolean,
	settlementType: LaytimeSettlementTypes,
): Items => {
	const keyItems = [createCargoLinkField(cargo), createLaytimeTermsField(cargo)];

	const items = [
		...createCargoPortItems(cargoPort, cargo, cargoPort.laytimeFrom, cargoPort.laytimeTo),
		createLaytimeAllowedField(
			'estimatedTimeAllowed',
			normalize(cargoPort.estimatedTimeAllowed),
			'Calculated laytime allowed',
			cargo,
			cargoPort,
		),
		createLaytimeUsedField(normalize(cargoPort.timeUsed), cargo, cargoPort),
		createTimeDifferenceField(
			normalize(cargoPort.estimatedTimeAllowed),
			normalize(cargoPort.timeUsed),
			cargo,
			cargoPort,
		),
	];

	const totalDemurrage = normalize(cargoPort.calculatedDemurrage);
	const totalDespatch = normalize(cargoPort.calculatedDespatch);

	const isDemurrage = totalDemurrage > totalDespatch;

	const demurrageDespatchItem = {
		key: 'demurrageDespatch',
		label: isDemurrage ? getDemurrageTermLabel(cargo.demurrageTerm) : 'Despatch',
		value: isDemurrage ?
			cargo.stats?.totalDemurrage :
			cargo.stats?.totalDespatch,
		render: () => (
			<b style={{ textDecoration: cargoPort.settlementAmount != null ? 'line-through' : undefined }}>
				{formatCurrency(
					Math.abs(isDemurrage ? totalDemurrage : totalDespatch),
					cargo.currency,
				)}
			</b>
		),
	} as EditableDetailsItem<Partial<CargoProps>>;

	items.push(demurrageDespatchItem);
	items.push(createSettlementField(
		cargo,
		cargoPort,
		handleCargoChange,
		handleCargoPortChange,
		isSmall,
		settlementType,
	));

	return {
		keyItems,
		cargoPortItems: {
			id: cargoPort.id,
			port: cargoPort.port,
			portAndActionKey: cargoPort.portAndActionKey,
			items,
		},
	};
};

export const getReversibleFields = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort: CargoPortWithTurnDays,
	handleCargoChange: (values: Partial<CargoWithPortsAndCalculationsJSON>, shouldSave?: boolean) => void,
	handleCargoPortChange: (values: Partial<UpdateCargoPortRequest['attributes']>, shouldSave?: boolean) => void,
	isSmall: boolean,
	settlementType: LaytimeSettlementTypes,
): Items => {
	const keyItems = [
		createCargoLinkField(cargo),
		createLaytimeTermsField(cargo),
		createLaytimeAllowedField('timeAllowed', normalize(cargo.timeAllowed), 'Laytime allowed', cargo, cargoPort),
		createLaytimeUsedField(normalize(cargo.timeUsed), cargo, cargoPort),
		createTimeDifferenceField(
			normalize(cargo.timeAllowed),
			normalize(cargo.timeUsed),
			cargo,
			cargoPort,
		),
		{
			key: 'demurrage',
			label: `${getDemurrageTermLabel(cargo.demurrageTerm)} rate`,
			type: 'number',
			value: cargo.demurrage,
			editable: true,
			inputProps: {
				'data-testid': 'demurrageRateReversible',
				suffix: `${cargo.currency} / ${cargo.demurrageDespatchInterval}`,
			},
			render: () => formatCurrency(cargo.demurrage ?? 0, cargo.currency),
		} as EditableDetailsItem<Partial<CargoProps>>,
		{
			key: 'despatch',
			label: 'Despatch rate',
			type: 'number',
			value: cargo.despatch,
			editable: true,
			inputProps: {
				'data-testid': 'despatchRateReversible',
				suffix: `${cargo.currency} / ${cargo.demurrageDespatchInterval}`,
			},
			render: () => formatCurrency(cargo.despatch ?? 0, cargo.currency),
		} as EditableDetailsItem<Partial<CargoProps>>,
		createSettlementField(
			cargo,
			cargoPort,
			handleCargoChange,
			handleCargoPortChange,
			isSmall,
			settlementType,
		),
	];

	return {
		keyItems,
		cargoPortItems: {
			id: cargoPort.id,
			port: cargoPort.port,
			portAndActionKey: cargoPort.portAndActionKey,
			items: createCargoPortItems(cargoPort, cargo, cargoPort.laytimeFrom, cargoPort.laytimeTo),
		},
	};
};

export const getAveragedFields = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPort: CargoPortWithTurnDays,
	action: 'loading' | 'discharging' | undefined,
	handleCargoChange: (values: Partial<CargoWithPortsAndCalculationsJSON>, shouldSave?: boolean) => void,
	handleCargoPortChange: (values: Partial<UpdateCargoPortRequest['attributes']>, shouldSave?: boolean) => void,
	isSmall: boolean,
	settlementType: LaytimeSettlementTypes,
): Items => {
	const isLoading = action === 'loading';

	const keyItems = [
		createCargoLinkField(cargo),
		createLaytimeTermsField(cargo),
		createLaytimeAllowedField(
			isLoading ? 'timeAllowedLoading' : 'timeAllowedDischarging',
			normalize(isLoading ? cargo.timeAllowedLoading : cargo.timeAllowedDischarging),
			`Laytime allowed ${action}`,
			cargo,
			cargoPort,
		),
		createLaytimeUsedField(
			normalize(isLoading ? cargo.timeUsedLoading : cargo.timeUsedDischarging),
			cargo,
			cargoPort,
			`Laytime used ${action}`,
		),
		createTimeDifferenceField(
			normalize(isLoading ? cargo.timeAllowedLoading : cargo.timeAllowedDischarging),
			normalize(isLoading ? cargo.timeUsedLoading : cargo.timeUsedDischarging),
			cargo,
			cargoPort,
		),
		{
			key: isLoading ? 'demurrageLoading' : 'demurrageDischarge',
			label: `${getDemurrageTermLabel(cargo.demurrageTerm)} rate ${action}`,
			type: 'number',
			value: isLoading ? cargo.demurrageLoading : cargo.demurrageDischarge,
			editable: true,
			inputProps: {
				'data-testid': isLoading ? 'demurrageLoading' : 'demurrageDischarge',
				suffix: cargo.currency,
			},
			render: () => `${(isLoading ? cargo.demurrageLoading : cargo.demurrageDischarge) ?? 0} ${cargo.currency}`,
		} as EditableDetailsItem<Partial<CargoProps>>,
		{
			key: isLoading ? 'despatchLoading' : 'despatchDischarge',
			label: `Despatch rate ${action}`,
			type: 'number',
			value: isLoading ? cargo.despatchLoading : cargo.despatchDischarge,
			editable: true,
			inputProps: {
				'data-testid': isLoading ? 'despatchLoading' : 'despatchDischarge',
				suffix: cargo.currency,
			},
			render: () => `${(isLoading ? cargo.despatchLoading : cargo.despatchDischarge) ?? 0} ${cargo.currency}`,
		} as EditableDetailsItem<Partial<CargoProps>>,
		createSettlementField(
			cargo,
			cargoPort,
			handleCargoChange,
			handleCargoPortChange,
			isSmall,
			settlementType,
		)
	];

	const items = createCargoPortItems(cargoPort, cargo, cargoPort.laytimeFrom, cargoPort.laytimeTo);

	return {
		keyItems,
		cargoPortItems: {
			id: cargoPort.id,
			port: cargoPort.port,
			portAndActionKey: cargoPort.portAndActionKey,
			items,
		},
	};
};
